// @flow

import * as React from "react";
import { withRouter } from "react-router-dom";

import { PageHeader, Table, Tabs, FlashMessageConsumer, Preloader } from "buba-components";
import PageLayout, { PageLayoutScrollContext } from "buba-components/src/components/PageLayout";
import DataFetcher from "buba-components/src/components/DataFetcher";
import snakeCase from "lodash/snakeCase";
import isNull from "lodash/isNull";
import {
  getInquiryListParamsV2,
  saveInquiryListParamsV2,
  clearInquiryListParams
} from "buba-components/src/utils/inquiryListParams";

import styles from "./InquiryList.scss";
import paths from "../../data/paths";
import { getBankAdvisorInquiriesV2 } from "../../api/v2actions";
import { bankAdvisorInitialPayloadV2 } from "./payload";
import { getTableHeaders } from "./tableHeaders";
import { mapResponseForBankAdvisorV2 } from "./mappers";
import { getTableDataForBankAdvisor } from "./tableData";
import { tabsBankAdvisorV2 } from "./tabs";

export type ListType = "bankAdvisor";

type InquiriesListProps = {
  history: {
    push: string => void
  },
  type: ListType
};

const InquiriesListV2 = ({ history, type }: InquiriesListProps) => {
  const [sortedBy, setSortedBy] = React.useState("created_at");
  const [sortingDirection, setSortingDirection] = React.useState("desc");
  const initialPayload = bankAdvisorInitialPayloadV2;

  const createInitialPayload = () => {
    const inquiryListParams = getInquiryListParamsV2();

    const isSameInquiryType = isNull(inquiryListParams)
      ? false
      : history.location.pathname.includes(inquiryListParams.currentLocation);

    if (isSameInquiryType) {
      return {
        ...initialPayload,
        ...inquiryListParams,
        creator: snakeCase(type)
      };
    }

    clearInquiryListParams();

    return {
      ...initialPayload,
      creator: snakeCase(type)
    };
  };

  const initialPayloadSource = createInitialPayload();

  return (
    <PageLayout>
      <PageHeader header="Übersicht" bottomMargin />
      <FlashMessageConsumer>
        {({ addMessage }) => (
          <PageLayoutScrollContext.Consumer>
            {({ scrollToTop }) => (
              <DataFetcher
                initialPayload={initialPayloadSource}
                mapper={mapResponseForBankAdvisorV2}
                onError={msg => addMessage(msg, "failure")}
                apiAction={getBankAdvisorInquiriesV2}
              >
                {({ data, isLoading, requestFetch }) => {
                  if (!data) {
                    return null;
                  }

                  const { inquiries, pagination, tabCounts } = data;

                  const requestPageAndScroll = (page: number | null) => {
                    if (!page) {
                      return;
                    }

                    requestFetch({ page });
                    scrollToTop();
                  };

                  const requestSortingAndScroll = (columnId: string, direction: "asc" | "desc") => {
                    requestFetch({
                      sortBy: columnId,
                      sortDirection: direction
                    });

                    setSortedBy(columnId);
                    setSortingDirection(direction);
                  };

                  const tabs = tabsBankAdvisorV2;
                  const dashboardPath = paths.bankInquiryDashboardNew;

                  saveInquiryListParamsV2({
                    page: pagination.current,
                    currentLocation: history.location.pathname
                  });

                  return (
                    <Tabs
                      initialActiveIndex={initialPayloadSource.initialActiveIndex}
                      onChange={index => {
                        requestFetch({ status: tabs[index].id, page: 1 });
                        saveInquiryListParamsV2({
                          status: tabs[index].id,
                          initialActiveIndex: index
                        });
                      }}
                      tabs={tabs.map(tab => ({
                        title: () => tab.title,
                        content: () => {
                          switch (true) {
                            case isLoading:
                              return (
                                <div className={styles.placeholder}>
                                  <Preloader />
                                </div>
                              );
                            case inquiries.length === 0:
                              return (
                                <div className={styles.placeholder}>
                                  Es sind keine Anfragen mit diesem Status verfügbar.
                                </div>
                              );
                            default:
                              return (
                                <Table
                                  tableHeaders={getTableHeaders(
                                    requestSortingAndScroll,
                                    type,
                                    tab.id
                                  )}
                                  data={getTableDataForBankAdvisor(inquiries)}
                                  maxPages={pagination.totalPages}
                                  site="bank"
                                  onInquiryClick={id => history.push(dashboardPath(id))}
                                  currentPage={pagination.current}
                                  getNextPage={() => requestPageAndScroll(pagination.next)}
                                  getPreviousPage={() => requestPageAndScroll(pagination.prev)}
                                  sortedBy={sortedBy}
                                  sortingDirection={sortingDirection}
                                />
                              );
                          }
                        },
                        amount: tabCounts[tab.id]
                      }))}
                    />
                  );
                }}
              </DataFetcher>
            )}
          </PageLayoutScrollContext.Consumer>
        )}
      </FlashMessageConsumer>
    </PageLayout>
  );
};

export default withRouter(InquiriesListV2);
